import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, RefreshCw, Users, CheckCircle2, XCircle } from "lucide-react";
import { adminApi, formatApiError } from "@/lib/adminApi";

const getStatus = async () => (await adminApi.get("/admin/smartemailing/status")).data;
const syncAll = async () => (await adminApi.post("/admin/smartemailing/sync")).data;
const sendCampaign = async (spots) =>
  (await adminApi.post("/admin/smartemailing/send-campaign", null, { params: { spots } })).data;

export function SmartEmailingPanel({ spots }) {
  const [st, setSt] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    setErr("");
    getStatus()
      .then(setSt)
      .catch((e) => setErr(formatApiError(e?.response?.data?.detail)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const doSync = async () => {
    setBusy("sync");
    try {
      const r = await syncAll();
      toast.success(`Do SmartEmailingu synchronizováno ${r.imported} kontaktů.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const doSend = async () => {
    const n = st?.contacts ?? 0;
    if (!window.confirm(`Odeslat pozvánku (${spots} míst) VŠEM ${n} kontaktům v seznamu „${st?.list_name}“ přes SmartEmailing?`)) return;
    setBusy("send");
    try {
      const r = await sendCampaign(spots);
      toast.success(`Kampaň odeslána (č. ${r.campaign_id}). Statistiky najdete v SmartEmailingu.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-brand-surface border border-brand-accent/40 p-5 space-y-4" data-testid="smartemailing-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted">SmartEmailing · vaše databáze</div>
          <div className="mt-2 flex items-center gap-2 font-display font-black text-xl" data-testid="smartemailing-status">
            {err ? (
              <>
                <XCircle size={18} className="text-red-400" /> Nepřipojeno
              </>
            ) : st ? (
              <>
                <CheckCircle2 size={18} className="text-brand-accent" /> Připojeno · účet {st.account_id}
              </>
            ) : (
              "Načítám..."
            )}
          </div>
          {err && <div className="text-xs text-red-400 mt-1" data-testid="smartemailing-error">{err}</div>}
          {st && (
            <div className="mt-1.5 text-xs text-brand-muted leading-relaxed">
              Seznam <span className="text-white">{st.list_name}</span> ·{" "}
              <span className="text-white" data-testid="smartemailing-contacts">{st.contacts}</span> aktivních kontaktů ·{" "}
              {st.site_contacts} unikátních e-mailů z rezervací na webu · odesílatel {st.sender_name} &lt;{st.sender_email}&gt;
              <br />
              Každý nový zákazník, který se zarezervuje, se do seznamu přidá automaticky.
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="smartemailing-refresh"
            className="h-9 px-3 border border-brand-border hover:border-brand-accent hover:text-brand-accent transition-colors text-xs inline-flex items-center gap-2"
          >
            <RefreshCw size={13} /> Obnovit
          </button>
          <button
            onClick={doSync}
            disabled={!st || !!busy}
            data-testid="smartemailing-sync"
            className="h-9 px-3 border border-brand-border hover:border-brand-accent hover:text-brand-accent transition-colors text-xs inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Users size={13} /> {busy === "sync" ? "Synchronizuji..." : "Synchronizovat kontakty"}
          </button>
          <button
            onClick={doSend}
            disabled={!st || !!busy || !st.contacts}
            data-testid="smartemailing-send"
            className="h-11 px-5 bg-brand-accent text-black font-bold hover:bg-brand-accentHover transition-colors text-sm inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Send size={15} /> {busy === "send" ? "Odesílám..." : `Odeslat pozvánku všem (${st?.contacts ?? 0})`}
          </button>
        </div>
      </div>

      {st?.campaigns?.length > 0 && (
        <div className="border-t border-brand-border pt-3" data-testid="smartemailing-campaigns">
          <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted mb-2">Odeslané kampaně</div>
          {st.campaigns.map((c) => (
            <div key={`${c.campaign_id}-${c.created_at}`} className="flex justify-between gap-4 text-sm py-1.5 border-b border-brand-border/60 last:border-0">
              <span className="text-white truncate">
                <span className="text-brand-muted">#{c.campaign_id}</span> {c.subject}
              </span>
              <span className="text-brand-muted whitespace-nowrap">{new Date(c.created_at).toLocaleString("cs-CZ")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
